import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardMedia,
  CardContent,
  Button,
} from '@mui/material';
import {
  MusicNote,
  OpenInNew,
  SentimentSatisfied,
} from '@mui/icons-material';
import { getMusicByMood, getMoodSummary } from '../services/api';
import { useNavigate } from 'react-router-dom';

const MOOD_LABELS = {
  1: { label: 'Low & Struggling',   color: '#6366f1' },
  2: { label: 'A Bit Down',         color: '#22d3ee' },
  3: { label: 'Balanced & Chill',   color: '#4ade80' },
  4: { label: 'Good Vibes',         color: '#fbbf24' },
  5: { label: 'Feeling Amazing',    color: '#f472b6' },
};

export default function Music() {
  const navigate = useNavigate();

  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [moodScore,  setMoodScore]  = useState(3);
  const [moodLabel,  setMoodLabel]  = useState('');
  const [message,    setMessage]    = useState('');
  const [playlists,  setPlaylists]  = useState([]);
  const [activeScore, setActiveScore] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    loadMusic();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMusic(score = null) {
    try {
      setLoading(true);
      setError('');

      // If no score passed, get the user's latest mood score
      let finalScore = score;
      if (!finalScore) {
        try {
          const summaryRes = await getMoodSummary();
          const avg = summaryRes.data?.average_score;
          finalScore = avg ? Math.round(avg) : 3;
        } catch {
          finalScore = 3;
        }
      }

      setMoodScore(finalScore);
      setActiveScore(finalScore);

      // Fetch Spotify recommendations
      const res = await getMusicByMood(finalScore);
      setMoodLabel(res.data?.mood_label ?? '');
      setMessage(res.data?.message ?? '');
      setPlaylists(res.data?.playlists ?? []);

    } catch (err) {
      setError('Could not load music recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const accentColor = MOOD_LABELS[moodScore]?.color ?? '#4ade80';

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1628 0%, #0d1f3c 50%, #0a2040 100%)',
      px: { xs: 2, sm: 4 },
      py: 4,
      color: '#e8f4f8',
    }}>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontSize: { xs: '1.8rem', sm: '2.2rem' },
          fontWeight: 800,
          background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: '-0.5px',
        }}>
          Wellness Music 🎵
        </Typography>
        <Typography sx={{ color: '#7fa8c8', fontSize: '0.9rem', mt: 0.5 }}>
          Spotify playlists matched to your mood
        </Typography>
      </Box>

      {/* Mood Score Selector */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ color: '#7fa8c8', fontSize: '0.85rem', mb: 1.5 }}>
          Pick a mood to get recommendations:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(MOOD_LABELS).map(([score, { label, color }]) => (
            <Chip
              key={score}
              label={label}
              onClick={() => loadMusic(Number(score))}
              sx={{
                background: activeScore === Number(score)
                  ? `${color}22`
                  : 'rgba(255,255,255,0.05)',
                color: activeScore === Number(score) ? color : '#7fa8c8',
                border: activeScore === Number(score)
                  ? `1px solid ${color}55`
                  : '1px solid rgba(255,255,255,0.08)',
                fontWeight: activeScore === Number(score) ? 700 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { background: `${color}15`, color },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{
          mb: 3,
          background: 'rgba(239,68,68,0.08)',
          color: '#fca5a5',
          border: '1px solid rgba(239,68,68,0.2)',
        }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#4ade80' }} />
        </Box>
      )}

      {/* Content */}
      {!loading && (
        <>
          {/* Mood message */}
          {moodLabel && (
            <Box sx={{
              mb: 3,
              p: 2.5,
              borderRadius: 3,
              background: `${accentColor}12`,
              border: `1px solid ${accentColor}30`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <SentimentSatisfied sx={{ color: accentColor, fontSize: 20 }} />
                <Typography sx={{ color: accentColor, fontWeight: 700, fontSize: '1rem' }}>
                  {moodLabel}
                </Typography>
              </Box>
              <Typography sx={{ color: '#7fa8c8', fontSize: '0.875rem' }}>
                {message}
              </Typography>
            </Box>
          )}

          {/* Playlists */}
          {playlists.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: '#7fa8c8' }}>
              <MusicNote sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
              <Typography>No playlists found for this mood.</Typography>
              <Typography sx={{ fontSize: '0.85rem', mt: 1 }}>
                Try a different mood above.
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 2,
            }}>
              {playlists.map((playlist, idx) => (
                <Card key={idx} sx={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: `1px solid ${accentColor}40`,
                    transform: 'translateY(-4px)',
                    background: `${accentColor}08`,
                  },
                }}>
                  {/* Playlist Image */}
                  {playlist.image_url ? (
                    <CardMedia
                      component="img"
                      height="180"
                      image={playlist.image_url}
                      alt={playlist.name}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box sx={{
                      height: 180,
                      background: `${accentColor}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <MusicNote sx={{ fontSize: 64, color: accentColor, opacity: 0.5 }} />
                    </Box>
                  )}

                  <CardContent sx={{ p: 2 }}>
                    {/* Playlist Name */}
                    <Typography sx={{
                      color: '#e8f4f8',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      mb: 0.5,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {playlist.name}
                    </Typography>

                    {/* Owner */}
                    <Typography sx={{
                      color: '#7fa8c8',
                      fontSize: '0.75rem',
                      mb: 1.5,
                    }}>
                      by {playlist.owner}
                    </Typography>

                    {/* Open in Spotify Button */}
                    <Button
                      fullWidth
                      href={playlist.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                      sx={{
                        background: `linear-gradient(135deg, ${accentColor}, #22d3ee)`,
                        color: '#0a1628',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        borderRadius: 2,
                        py: 0.8,
                        textTransform: 'none',
                        '&:hover': { opacity: 0.85 },
                      }}
                    >
                      Open in Spotify
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}